// Analytics consent shared with the xeo.vision website: same `xv_consent` cookie on .xeo.vision,
// same Clarity signal. SalesViewer runs on legitimate interest regardless, as on the website.
type Consent = 'granted' | 'denied';

const MAX_AGE = 180 * 24 * 60 * 60;
// Set by the website's GA4, Clarity and HubSpot tags on .xeo.vision, plus Clarity in the app.
const ANALYTICS_COOKIES = [
  '_ga',
  '_clck',
  '_clsk',
  '__hstc',
  '__hssc',
  '__hssrc',
  'hubspotutk',
  'messagesUtk',
];

function readConsent(): Consent | null {
  return (
    (/(?:^|;\s*)xv_consent=(granted|denied)(?:;|$)/.exec(document.cookie)?.[1] as Consent) ?? null
  );
}

function writeConsent(value: Consent) {
  const domain = /(^|\.)xeo\.vision$/.test(location.hostname) ? '; Domain=xeo.vision' : '';
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `xv_consent=${value}; Max-Age=${MAX_AGE}; Path=/${domain}; SameSite=Lax${secure}`;
}

function expireAnalyticsCookies() {
  const names = [
    ...ANALYTICS_COOKIES,
    ...document.cookie
      .split(';')
      .map((entry) => entry.split('=')[0].trim())
      .filter((name) => name.startsWith('_ga_')),
  ];
  // Vendors set cookies on the registrable domain; try the host and every parent.
  const parts = location.hostname.split('.');
  const domains = ['', ...parts.slice(0, -1).map((_, i) => `; Domain=${parts.slice(i).join('.')}`)];
  for (const name of names) {
    for (const domain of domains) document.cookie = `${name}=; Max-Age=0; Path=/${domain}`;
  }
}

// Queued before the Clarity tag loads, so Clarity stays cookieless until analytics consent.
export function signalClarity(value: Consent) {
  const w = window as any;
  w.clarity =
    w.clarity ||
    function () {
      // Clarity's queue stub, as in the vendor snippet
      // eslint-disable-next-line prefer-rest-params
      (w.clarity.q = w.clarity.q || []).push(arguments);
    };
  // Advertising storage stays denied: granting it shares Clarity data with Microsoft Ads.
  w.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: value });
}

export function useConsent() {
  const choice = useState<Consent | null>('consent', readConsent);
  // Inside a third-party iframe the SameSite=Lax cookie can't be stored, so the embed stays cookieless.
  const bannerVisible = useState(
    'consent-banner',
    () => !choice.value && window.self === window.top,
  );

  function set(next: Consent) {
    const previous = choice.value;
    choice.value = next;
    bannerVisible.value = false;
    writeConsent(next);
    if (next === previous) return;

    signalClarity(next);
    if (previous === 'granted') {
      // Withdrawal: running tags keep their state, so clear their cookies and start over.
      expireAnalyticsCookies();
      location.reload();
    }
  }

  return { choice, bannerVisible, set };
}
